import React from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Heading,
  Text,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Grid,
  GridItem,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
} from '@chakra-ui/react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

interface AmortizationEntry {
  paymentNumber: number;
  paymentAmount: number;
  principalPaid: number;
  interestPaid: number;
  remainingBalance: number;
  totalInterestPaid: number;
  totalPrincipalPaid: number;
}

interface AmortizationScheduleProps {
  loanAmount: number;
  interestRate: number;
  amortizationYears: number;
  paymentFrequency: string;
  monthlyPayment: number;
}

const calculateAmortizationSchedule = (props: AmortizationScheduleProps): AmortizationEntry[] => {
  const {
    loanAmount,
    interestRate,
    amortizationYears,
    paymentFrequency,
  } = props;

  const schedule: AmortizationEntry[] = [];
  let remainingBalance = loanAmount;
  let totalInterestPaid = 0;
  let totalPrincipalPaid = 0;

  // Convert annual rate to monthly
  const monthlyRate = (interestRate / 100) / 12;
  
  // Calculate number of payments
  const paymentsPerYear = paymentFrequency === 'monthly' ? 12 :
    paymentFrequency === 'biweekly' ? 26 :
    paymentFrequency === 'accelerated_biweekly' ? 26 : 52;
  
  const totalPayments = amortizationYears * paymentsPerYear;
  
  // Calculate payment amount based on frequency
  const paymentAmount = props.monthlyPayment * (paymentFrequency === 'monthly' ? 1 :
    paymentFrequency === 'biweekly' ? 12/26 :
    paymentFrequency === 'accelerated_biweekly' ? 12/24 : 12/52);

  for (let i = 1; i <= totalPayments && remainingBalance > 0; i++) {
    const interestPaid = remainingBalance * monthlyRate * (12/paymentsPerYear);
    const principalPaid = paymentAmount - interestPaid;
    remainingBalance -= principalPaid;
    totalInterestPaid += interestPaid;
    totalPrincipalPaid += principalPaid;

    if (i <= 12 || i % 12 === 0 || i === totalPayments) { // Store yearly data points
      schedule.push({
        paymentNumber: i,
        paymentAmount,
        principalPaid,
        interestPaid,
        remainingBalance: Math.max(0, remainingBalance),
        totalInterestPaid,
        totalPrincipalPaid,
      });
    }
  }

  return schedule;
};

const COLORS = ['#E31837', '#0066CC', '#00A86B'];

const AmortizationSchedule: React.FC<AmortizationScheduleProps> = (props) => {
  const schedule = calculateAmortizationSchedule(props);

  const pieData = [
    { name: 'Principal', value: props.loanAmount },
    { name: 'Total Interest', value: schedule[schedule.length - 1].totalInterestPaid },
  ];

  return (
    <Box mt={8}>
      <Heading size="md" mb={4} color="deebits.red">Amortization Analysis</Heading>
      
      <Tabs variant="enclosed" colorScheme="red">
        <TabList>
          <Tab>Balance Over Time</Tab>
          <Tab>Payment Breakdown</Tab>
          <Tab>Schedule</Tab>
        </TabList>

        <TabPanels>
          <TabPanel>
            <Box height="450px">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={schedule}
                  margin={{ top: 20, right: 30, left: 60, bottom: 45 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis 
                    dataKey="paymentNumber"
                    tickCount={10}
                    label={{ 
                      value: 'Payment Period (Months)', 
                      position: 'bottom',
                      offset: 35,
                      style: { textAnchor: 'middle' }
                    }}
                  />
                  <YAxis 
                    tickFormatter={(value) => `$${value.toLocaleString()}`}
                    label={{ 
                      value: 'Amount in Canadian Dollars ($)', 
                      angle: -90, 
                      position: 'insideLeft',
                      offset: -45,
                      style: { textAnchor: 'middle' }
                    }}
                  />
                  <Tooltip 
                    formatter={(value: number) => [`$${value.toLocaleString()}`, '']}
                    labelFormatter={(label) => `Month ${label}`}
                    contentStyle={{
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      padding: '8px'
                    }}
                  />
                  <Legend 
                    verticalAlign="bottom"
                    height={30}
                    wrapperStyle={{
                      paddingTop: "12px",
                      marginTop: "10px",
                      borderTop: "1px solid #e0e0e0"
                    }}
                    iconSize={10}
                    iconType="plainline"
                  />
                  <Line
                    type="monotone"
                    dataKey="remainingBalance"
                    name="Remaining Balance"
                    stroke="#E31837"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="totalInterestPaid"
                    name="Total Interest Paid"
                    stroke="#0066CC"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="totalPrincipalPaid"
                    name="Total Principal Paid"
                    stroke="#00A86B"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </TabPanel>

          <TabPanel>
            <Box height="400px">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={150}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => ['$' + value.toFixed(2)]} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </TabPanel>

          <TabPanel>
            <Box overflowX="auto">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Payment #</Th>
                    <Th isNumeric>Payment</Th>
                    <Th isNumeric>Principal</Th>
                    <Th isNumeric>Interest</Th>
                    <Th isNumeric>Remaining Balance</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {schedule.map((entry) => (
                    <Tr key={entry.paymentNumber}>
                      <Td>{entry.paymentNumber}</Td>
                      <Td isNumeric>${entry.paymentAmount.toFixed(2)}</Td>
                      <Td isNumeric>${entry.principalPaid.toFixed(2)}</Td>
                      <Td isNumeric>${entry.interestPaid.toFixed(2)}</Td>
                      <Td isNumeric>${entry.remainingBalance.toFixed(2)}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </TabPanel>
        </TabPanels>
      </Tabs>

      <Box mt={4} p={6} bg="white" borderRadius="lg" boxShadow="sm" border="1px" borderColor="gray.100">
        <Heading size="sm" mb={4} color="gray.700">Loan Summary</Heading>
        <StatGroup>
          <Grid templateColumns="repeat(3, 1fr)" gap={6} width="100%">
            <Stat>
              <StatLabel color="gray.600">Principal Amount</StatLabel>
              <StatNumber color="deebits.red" fontSize="xl">
                ${props.loanAmount.toLocaleString()}
              </StatNumber>
            </Stat>
            <Stat>
              <StatLabel color="gray.600">Total Interest</StatLabel>
              <StatNumber color="blue.600" fontSize="xl">
                ${schedule[schedule.length - 1].totalInterestPaid.toLocaleString()}
              </StatNumber>
            </Stat>
            <Stat>
              <StatLabel color="gray.600">Total Cost of Loan</StatLabel>
              <StatNumber color="green.600" fontSize="xl">
                ${(props.loanAmount + schedule[schedule.length - 1].totalInterestPaid).toLocaleString()}
              </StatNumber>
            </Stat>
          </Grid>
        </StatGroup>
        <Grid templateColumns="repeat(3, 1fr)" gap={6} mt={4}>
          <Box>
            <Text fontSize="sm" color="gray.500">Monthly Payment</Text>
            <Text fontSize="md" fontWeight="medium" color="gray.700">
              ${props.monthlyPayment.toLocaleString()}
            </Text>
          </Box>
          <Box>
            <Text fontSize="sm" color="gray.500">Interest Rate</Text>
            <Text fontSize="md" fontWeight="medium" color="gray.700">
              {props.interestRate}%
            </Text>
          </Box>
          <Box>
            <Text fontSize="sm" color="gray.500">Amortization Period</Text>
            <Text fontSize="md" fontWeight="medium" color="gray.700">
              {props.amortizationYears} years
            </Text>
          </Box>
        </Grid>
      </Box>
    </Box>
  );
};

export default AmortizationSchedule; 